import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Cpu, SearchCheck, Plane, MessagesSquare,
  Building2, Megaphone, Boxes, ShieldCheck, Gauge, Zap,
  Compass, Play, Share2, Star, ChevronRight,
} from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import Marquee from "@/components/Marquee";
import LiveCounter from "@/components/LiveCounter";
import HeroBoardPreview from "@/components/HeroBoardPreview";
import { ScoreDonut } from "@/components/SignalBadges";
import { RevealLines, FadeUp } from "@/components/Reveal";

const CATEGORY_ICONS = {
  AI: Cpu, SEO: SearchCheck, Travel: Plane, Social: MessagesSquare,
  "Real Estate": Building2, Marketing: Megaphone, Others: Boxes,
};

const CATEGORY_BLURBS = {
  AI: "Models, tools & applied AI",
  SEO: "Agencies, audits & tooling",
  Travel: "Operators, agents & platforms",
  Social: "Community & social brands",
  "Real Estate": "Property services & proptech",
  Marketing: "Studios, media & growth",
  Others: "Everything else on the board",
};

const CHAPTERS = [
  {
    num: "01",
    title: "DUAL-SIGNAL INTEGRITY",
    body: "The global Top 3 is manually curated by HighRank from Claimed + Verified businesses. Below that, board order follows reputation and activity. Boost controls Top Spot placement only and never moves the leaderboard or HighRank Score.",
  },
  {
    num: "02",
    title: "REAL ENDORSEMENTS, NO FLUFF",
    body: "Every rating, written review and thumbs up comes from a signed-in account — one review and one endorsement per person per business. No seeded praise, no imported ratings, no astroturf. If a business has no reputation signals, we say so — 'Not yet scored' beats a fake zero.",
  },
  {
    num: "03",
    title: "A GLOBAL STAGE, REGIONAL LENSES",
    body: "One board, four lenses: US, UK, India and Global. Tech startups and businesses share the same stage — filter by category and see who actually ranks where you compete.",
  },
];

export default function Home() {
  const [meta, setMeta] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [primeCompany, setPrimeCompany] = useState(null);
  const [homeVideoCompanies, setHomeVideoCompanies] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/companies").then((r) => {
      const items = r.data.items || [];
      setCompanies(items);
      const hvList = items.filter((c) => c.home_screen_video && c.home_video_url);
      if (hvList.length > 0) setHomeVideoCompanies(hvList);
    }).catch(() => {});
    api.get("/boost/prime").then((r) => setPrimeCompany(r.data.prime_company || null)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Seo
        title="HighRank — Get Discovered. Stay Ranked."
        siteName="HighRank"
        description="The discovery leaderboard for startups and businesses. Rank Position shows visibility, HighRank Score (0–100) shows reputation — two signals, never blended."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "HighRank",
          description: "Public leaderboard where startups and businesses get discovered — Rank Position for visibility, HighRank Score for reputation.",
        }}
      />

      <div className="pt-28 md:pt-16">
        <Marquee />
      </div>

      {/* HERO */}
      <section ref={heroRef} className="relative sm:min-h-[58vh] flex items-center overflow-hidden">
        <motion.div style={{ y: gridY }} className="absolute inset-0 bg-grid opacity-60" />
        <motion.div
          style={{ y: orbY }}
          className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-emerald-500/15 blur-[120px]"
        />
        <div className="absolute bottom-0 -left-40 w-[26rem] h-[26rem] rounded-full bg-cyan-500/10 blur-[110px]" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#050505] to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full grid lg:grid-cols-[minmax(0,1fr)_430px] gap-6 lg:gap-10 items-center">
          <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="font-mono2 text-[11px] sm:text-xs uppercase tracking-[0.35em] text-emerald-400 mb-3"
          >
            {"// The leaderboard for startups & businesses"}
          </motion.p>

          <RevealLines
            data-testid="hero-title"
            className="font-display uppercase leading-[0.92] tracking-wide text-[11vw] sm:text-6xl lg:text-[4.5rem]"
            lines={[
              <span key="a" className="text-slate-100">Get discovered.</span>,
              <span key="b" className="text-emerald-400 text-glow">Stay ranked.</span>,
            ]}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-4 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
          >
            A public leaderboard for startups and businesses — two signals, zero confusion.{" "}
            <span className="text-amber-400 font-semibold">Boost</span> controls Top Spot placement.{" "}
            <span className="text-emerald-400 font-semibold">Real reviews & endorsements</span> move your HighRank Score.
            Never blended.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-4"
          >
            <LiveCounter />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            data-testid="hero-stats-row"
            className="relative z-10 mt-5 grid grid-cols-2 gap-x-8 gap-y-3 pb-2 sm:flex sm:flex-wrap sm:gap-x-10 sm:pb-0"
          >
            {[
              [meta?.total_companies ?? "—", "PRODUCTS LISTED"],
              [meta?.total_upvotes ?? "—", "ENDORSEMENTS"],
              ["7", "CATEGORIES"],
              ["4", "REGIONS"],
            ].map(([v, label]) => (
              <div key={label}>
                <p className="font-mono2 text-xl sm:text-3xl font-bold text-slate-100">{v}</p>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-0.5">{label}</p>
              </div>
            ))}
          </motion.div>
          </div>

          <HeroBoardPreview companies={companies} primeCompany={primeCompany} homeVideoCompanies={homeVideoCompanies} />
        </div>

        <div
          aria-hidden="true"
          className="hidden sm:block absolute bottom-1 left-1/2 -translate-x-1/2 font-display text-[18vw] leading-none stroke-text opacity-20 pointer-events-none select-none"
        >
          HIGHRANK
        </div>
      </section>

      {/* TRENDING CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <FadeUp>
          <div className="flex items-end justify-between gap-4 mb-5">
            <div>
              <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-400 mb-2">{"// Trending"}</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-wide text-slate-100">
                Browse by category
              </h2>
            </div>
            <Link
              to="/leaderboard"
              data-testid="categories-view-all-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-emerald-400 transition-colors"
            >
              Full leaderboard <ArrowRight size={15} />
            </Link>
          </div>
        </FadeUp>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {(meta?.categories || []).map((cat, i) => {
            const Icon = CATEGORY_ICONS[cat.name] || Boxes;
            return (
              <FadeUp key={cat.name} delay={i * 0.05}>
                <Link
                  to={`/leaderboard?category=${encodeURIComponent(cat.name)}`}
                  data-testid={`category-card-${cat.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex flex-col items-start gap-4 rounded-xl border border-white/[0.07] bg-[#0A0A09] p-4 h-full hover:border-emerald-500/40 hover:bg-[#121210] hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(16,185,129,0.12)] transition-all duration-300"
                >
                  <Icon size={22} className="text-slate-400 group-hover:text-emerald-400 transition-colors duration-300" />
                  <div>
                    <p className="font-heading font-bold text-slate-100 text-sm sm:text-base">{cat.name}</p>
                    <p className="text-[11px] text-slate-400 leading-snug mt-0.5">{CATEGORY_BLURBS[cat.name]}</p>
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1.5">
                      {cat.count} listed
                    </p>
                  </div>
                </Link>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* DISCOVER FEATURE SECTION */}
      <section className="relative overflow-hidden border-y border-emerald-500/10 bg-[#050505]">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="absolute top-0 right-0 w-[32rem] h-[20rem] rounded-full bg-emerald-500/8 blur-[100px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
          <div className="grid lg:grid-cols-[1fr_400px] gap-10 lg:gap-16 items-center">

            {/* Left — copy */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 font-mono2 text-[10px] uppercase tracking-[0.25em] rounded-full px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  New feature
                </span>
              </div>

              <FadeUp>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-400 mb-2">{"// Discover"}</p>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-wide text-slate-100 leading-[0.95] mb-4">
                  Hand-picked.<br />
                  <span className="text-emerald-400">Featured.</span> Fresh.
                </h2>
                <p className="text-slate-400 text-base sm:text-lg leading-relaxed mb-8 max-w-lg">
                  A curated grid of businesses worth knowing about — with inline video previews,
                  shareable cards, and category filters. Updated weekly by our team.
                </p>
              </FadeUp>

              {/* Feature tiles */}
              <div className="grid sm:grid-cols-3 gap-3 mb-8">
                {[
                  {
                    icon: <Play size={16} className="text-emerald-400" />,
                    title: "Inline Videos",
                    body: "Scroll and watch. Videos auto-play as you browse — no taps needed.",
                  },
                  {
                    icon: <Share2 size={16} className="text-emerald-400" />,
                    title: "Share Cards",
                    body: "Every featured card ships with a branded 1200×630 preview for social media.",
                  },
                  {
                    icon: <Star size={16} className="text-emerald-400" />,
                    title: "Get Featured",
                    body: "Submit your business for a Discover slot and get seen by thousands.",
                  },
                ].map(({ icon, title, body }, i) => (
                  <FadeUp key={title} delay={i * 0.07}>
                    <div className="h-full rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 hover:border-emerald-500/25 hover:bg-white/[0.04] transition-all duration-300">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-3">
                        {icon}
                      </div>
                      <p className="font-heading font-bold text-slate-100 text-sm mb-1">{title}</p>
                      <p className="text-[12px] text-slate-400 leading-relaxed">{body}</p>
                    </div>
                  </FadeUp>
                ))}
              </div>

              <FadeUp delay={0.2}>
                <Link
                  to="/discover"
                  data-testid="home-discover-cta"
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-full px-7 py-3.5 transition-all duration-200 hover:shadow-[0_0_28px_rgba(16,185,129,0.5)]"
                >
                  <Compass size={16} /> Explore Discover <ChevronRight size={15} />
                </Link>
              </FadeUp>
            </div>

            {/* Right — visual card mockup */}
            <FadeUp delay={0.15}>
              <div className="relative hidden lg:block">
                {/* Cards fanned out like sticky notes */}
                <div className="relative h-[340px]">
                  {/* Card 3 — back */}
                  <div
                    className="absolute inset-0 rounded-2xl border border-white/10 bg-[#0A0A09] shadow-xl"
                    style={{ transform: "rotate(4deg) translateY(12px)", zIndex: 1 }}
                  />
                  {/* Card 2 — mid */}
                  <div
                    className="absolute inset-0 rounded-2xl border border-emerald-500/20 bg-[#0d0d0b] shadow-xl overflow-hidden"
                    style={{ transform: "rotate(-2deg) translateY(6px)", zIndex: 2 }}
                  >
                    <div className="w-full h-full bg-gradient-to-br from-emerald-900/30 to-transparent" />
                  </div>
                  {/* Card 1 — front (main) */}
                  <div
                    className="absolute inset-0 rounded-2xl border border-emerald-500/30 bg-[#0A0A09] shadow-2xl overflow-hidden"
                    style={{ zIndex: 3 }}
                  >
                    {/* Thumb pin */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 w-4 h-4 rounded-full bg-emerald-400 shadow-md border-2 border-white" />

                    {/* Real thumbnail image */}
                    <div className="relative h-[220px] overflow-hidden">
                      <img
                        src="https://images.unsplash.com/photo-1636819488537-a9b1ffb315ce?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600"
                        alt="Featured business"
                        className="w-full h-full object-cover"
                      />
                      {/* HR badge */}
                      <div className="absolute top-3 left-3 flex items-center gap-0.5 bg-emerald-500 rounded px-1.5 py-0.5 shadow-md">
                        <span className="text-[9px] font-black text-black tracking-tight leading-none">HR</span>
                        <ArrowRight size={9} className="text-black" strokeWidth={3} />
                      </div>
                      {/* Bottom gradient */}
                      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#0A0A09] to-transparent" />
                    </div>

                    {/* Card info */}
                    <div className="px-4 pb-4 pt-2">
                      <span className="text-[9px] font-semibold text-emerald-400/70 uppercase tracking-wide">Tech & Gadgets</span>
                      <p className="font-bold text-sm text-slate-100 mt-0.5">HighRank.Space</p>
                      <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">The discovery leaderboard for startups.</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-[10px] text-slate-400">👁 142</span>
                        <span className="text-[10px] text-slate-400">♡ 38</span>
                        <span className="ml-auto text-[10px] font-bold text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5">Featured</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* "What's new" callout */}
                <div className="mt-4 flex items-start gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] p-3.5">
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Zap size={13} className="text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-emerald-300 mb-0.5">What's new in Discover</p>
                    <p className="text-[11px] text-slate-200 leading-relaxed">
                      Full-screen video player with sound · Branded share cards · Category filters
                    </p>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* TWO SIGNALS */}
      <section className="border-y border-white/5 bg-[#070706]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <FadeUp>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-400 mb-2">{"// The system"}</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-wide text-slate-100 mb-6">
              Two signals. <span className="text-emerald-400">Zero confusion.</span>
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-2 gap-4">
            <FadeUp delay={0.05}>
              <div className="h-full rounded-2xl border border-amber-500/25 bg-gradient-to-b from-amber-500/[0.06] to-transparent p-6 sm:p-7">
                <div className="flex items-center justify-between mb-4">
                  <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-amber-400">Rank Position</p>
                  <Zap size={18} className="text-amber-400" />
                </div>
                <p className="font-mono2 text-6xl sm:text-7xl font-bold text-amber-400 mb-4">#07</p>
                <p className="font-heading text-lg font-semibold text-slate-100 mb-2">Where you sit on the board.</p>
                <p className="text-sm text-slate-400 leading-relaxed">
                  The global Top 3 is <span className="text-amber-400 font-semibold">manually curated</span> from
                  Claimed + Verified businesses. Below that, board order follows reputation and activity — Boost never
                  moves this leaderboard.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.12}>
              <div className="h-full rounded-2xl border border-emerald-500/25 bg-gradient-to-b from-emerald-500/[0.06] to-transparent p-6 sm:p-7">
                <div className="flex items-center justify-between mb-4">
                  <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-emerald-400">HighRank Score</p>
                  <Gauge size={18} className="text-emerald-400" />
                </div>
                <div className="flex items-center gap-5 mb-4">
                  <ScoreDonut score={94} size={80} stroke={7} testId="hero-score-demo" />
                  <p className="font-mono2 text-xs text-slate-400 leading-relaxed">
                    0–100 · EARNED,
                    <br />
                    NEVER BOUGHT
                  </p>
                </div>
                <p className="font-heading text-lg font-semibold text-slate-100 mb-2">What the market thinks of you.</p>
                <p className="text-sm text-slate-400 leading-relaxed mb-5">
                  An independent platform reputation score — not a Google rating, not a guarantee of quality. Moved
                  only by signed-in reviews, endorsements and profile quality.
                </p>
                <div className="space-y-3">
                  {[
                    ["Average star rating", 40, "bg-emerald-500"],
                    ["Written review volume", 20, "bg-cyan-500"],
                    ["Endorsements (thumbs up)", 20, "bg-emerald-300"],
                    ["Profile completeness", 20, "bg-slate-400"],
                  ].map(([label, pct, color]) => (
                    <div key={label}>
                      <div className="flex justify-between font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">
                        <span>{label}</span>
                        <span className="text-slate-300">{pct}%</span>
                      </div>
                      <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${pct}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
                          className={`h-full ${color} rounded-full`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <FadeUp>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-400 mb-2">{"// Manifesto"}</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-wide text-slate-100 mb-6">
            What we stand on
          </h2>
        </FadeUp>
        <div className="grid md:grid-cols-3 gap-4">
          {CHAPTERS.map((ch, i) => (
            <FadeUp key={ch.num} delay={i * 0.08}>
              <div className="group h-full rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-5 hover:border-emerald-500/30 transition-colors duration-300">
                <p className="font-mono2 text-5xl font-bold stroke-text group-hover:text-emerald-400 group-hover:[-webkit-text-stroke:0] transition-all duration-300 mb-3">
                  {ch.num}
                </p>
                <p className="font-heading text-lg font-bold text-slate-100 tracking-tight mb-2">{ch.title}</p>
                <p className="text-sm text-slate-400 leading-relaxed">{ch.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[36rem] h-[20rem] rounded-full bg-emerald-500/10 blur-[110px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 text-center">
          <FadeUp>
            <ShieldCheck size={28} className="text-emerald-400 mx-auto mb-3" />
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-wide text-slate-100 mb-3">
              Put your business <span className="text-emerald-400 text-glow">on the board</span>
            </h2>
            <p className="max-w-xl mx-auto text-slate-400 text-base sm:text-lg leading-relaxed mb-6">
              Built for tech startups and businesses alike. Listing is free during launch — claim your profile,
              earn real reviews and endorsements, and let your HighRank Score do the talking.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/add-business"
                data-testid="cta-add-business-button"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-full px-8 py-3.5 transition-all duration-200 hover:shadow-[0_0_30px_rgba(16,185,129,0.5)]"
              >
                List your business <ArrowRight size={17} />
              </Link>
              <Link
                to="/leaderboard"
                data-testid="cta-claim-link"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-emerald-500/50 text-slate-200 hover:text-emerald-400 font-semibold rounded-full px-8 py-3.5 transition-all duration-200"
              >
                Find & claim your listing
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
